<template>
  <div>
    <div class="d-flex align-items-center justify-content-between">
      <div class="d-flex justify-content-center align-items-center">
        <span class="small text-muted me-2">{{ selectedItems.length }}</span>
        <div class="pe-2" @click="emit('onClear')" role="button">
          <i class="bi bi-ban" title="clear selected"></i>
        </div>
        <div class="pe-2" @click="emit('onVisible')" role="button">
          <i class="bi bi-eye" title="select visible"></i>
        </div>
      </div>
      <div class="d-flex justify-content-center align-items-center flex-column flex-md-row">
        <select v-model="fadeDuration" class="form-select form-select-sm mx-md-1 my-1 my-md-0">
          <option v-for="option in [
              {label:'none (0)',value:10},
              {label:'short (.3s)', value:300},
              {label:'medium (.5s)', value:500},
              {label:'medium-long (1s)',value:1000},
              {label:'long (2s)',value:2000},
              {label:'super long (3s)',value:3000}
            ]" :value="option.value" :key="option.value">
            {{option.label}}
          </option>
        </select>
        <select v-model="holdDuration" class="form-select form-select-sm mx-md-1 my-1 my-md-0">
          <option v-for="option in [
                {label:'no delay',value:0},
                {label:'1/2 s',value:500},
                {label:'1 s',value:1000},
                {label:'1.5 s',value:1500},
                {label:'2 s',value:2000},
                {label:'3 s',value:3000},
                {label:'6 s', value:6000}
                ]" :value="option.value" :key="option.value">
            {{option.label}}
          </option>
        </select>
        <select v-model="transition" class="form-select form-select-sm mx-md-1 my-1 my-md-0">
          <option v-for="option in [
                {label:'Crossfade',value:'crossfade'},
                {label:'Slide', value:'slide'},
                {label: 'Drop', value:'drop'},
                {label: 'Scroll', value:'scroll'},
                {label: 'Random', value:'random'},
                ]" :value="option.value" :key="option.value">
            {{option.label}}
          </option>
        </select>
        <select v-model="ease" class="form-select form-select-sm mx-md-1 my-1 my-md-0">
          <option v-for="option in [
                {label:'Linear',value:'linear'},
                {label:'Ease In/Out', value:'sine.inOut'},
                {label:'Power 2 Out', value:'power2.out'},
                {label:'Rubber Band', value:'elastic'},
                {label:'ExpoScale', value:'expoScale'},
                {label:'Circle Out', value:'circ.out'},
                {label:'Bounce', value:'bounce.out'},
                {label:'Backwise', value:'back.inOut'},
                {label:'Slow Sling', value:'slow(0.7,0.7,true)'}
                ]" :value="option.value" :key="option.value">
            {{option.label}}
          </option>
        </select>
        <select v-model="aspectRatio" class="form-select form-select-sm mx-md-1 my-1 my-md-0">
          <option v-for="option in [
                {label:'Square',value:1.0},
                {label:'Portrait', value:16/9},
                {label:'Landscape', value:9/16},
                ]" :value="option.value" :key="option.value">
            {{option.label}}
          </option>
        </select>

        <input type="text" class="form-control form-control-sm mx-md-1 my-1 my-md-0" v-model="bgColor">

        <div class="small text-nowrap mx-md-1 my-1 my-md-0" role="button" @click="randomOrder=!randomOrder">
          <i class="bi" :class="{'bi-circle': !randomOrder, 'bi-check-circle': randomOrder}"></i> Random Order
        </div>

        <div class="small text-nowrap mx-md-1 my-1 my-md-0" role="button" @click="showTitle=!showTitle">
          <i class="bi" :class="{'bi-circle': !showTitle, 'bi-check-circle': showTitle}"></i> Title
        </div>

        <div class="small text-nowrap mx-md-1 my-1 my-md-0" role="button" @click="showPrice=!showPrice">
          <i class="bi" :class="{'bi-circle': !showPrice, 'bi-check-circle': showPrice}"></i> Price
        </div>

        <div class="small text-nowrap mx-md-1 my-1 my-md-0" role="button" @click="showSold=!showSold">
          <i class="bi" :class="{'bi-circle': !showSold, 'bi-check-circle': showSold}"></i> Sold
        </div>

        <div class="small text-nowrap mx-md-1 my-1 my-md-0" role="button" @click="record=!record">
          <i class="bi" :class="{'bi-circle': !record, 'bi-check-circle': record}"></i> Record
        </div>

        <button :disabled="disabled" type="button" class="btn btn-sm btn-primary mx-md-1 my-1 my-md-0" @click="slideShow=true">Slideshow</button>
      </div>
    </div>

    <app-modal :modal-in="slideShow" @close="slideShow=false">
      <template #header>Inventory Slideshow</template>
      <div v-if="slideShow">
        <inventory-slideshow
            :items="selectedItems"
            :fade-duration="fadeDuration"
            :hold-duration="holdDuration"
            :random-order="randomOrder"
            :aspect-ratio="aspectRatio"
            :show-title="showTitle"
            :show-price="showPrice"
            :show-sold="showSold"
            :bg-color="bgColor"
            :ease="ease"
            :record="record"
            :transition="transition"/>
      </div>
    </app-modal>
  </div>
</template>

<script setup>

import {computed, ref, defineProps, defineEmits, onMounted} from "vue";
import InventorySlideshow from "./InventorySlideshow.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";

const emit = defineEmits(["onClear"]);
const props = defineProps({
  selectedItems: {
    type: Array,
    default: () => []
  }
})

const slideShow = ref(false)
const holdDuration = ref(1000)
const fadeDuration = ref(500)
const transition = ref('crossfade')
const randomOrder = ref(false)
const aspectRatio = ref(1.0)
const showTitle = ref(true)
const showPrice = ref(true)
const showSold = ref(true)
const bgColor = ref('#cc0000')
const ease = ref('linear')
const record = ref(false)
const disabled = computed(() => props.selectedItems.length < 2)

onMounted(() => {
  const slideDefaults = JSON.parse(window.localStorage.getItem('esc_slideshow') || '{}')
  console.log(slideDefaults)
  holdDuration.value = slideDefaults?.holdDuration || 1000
  fadeDuration.value = slideDefaults?.fadeDuration || 500
  transition.value = slideDefaults?.transition || 'crossfade'
  randomOrder.value = !!slideDefaults?.randomOrder
  aspectRatio.value = parseFloat(slideDefaults?.aspectRatio || 1.0)
  showTitle.value = !!slideDefaults?.showTitle
  showPrice.value = !!slideDefaults?.showPrice
  showSold.value = !!slideDefaults?.showSold
  bgColor.value = slideDefaults?.bgColor || '#ffffff'
  ease.value = slideDefaults?.ease || 'linear'
  record.value = !!slideDefaults?.record
})

</script>
